import { useEffect, useRef } from 'react';
import { apiClient } from '../services/api/client';
import { playNotificationSound, sendBrowserNotification, updateTabTitle } from '../utils/videoNotifications';
import { useAuth } from './useAuth';

interface VideoGeneration {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  ownerId: string;
  ownerType: string;
  createdAt: string;
  modifiedAt: string;
  videoUrl?: string;
  errorMessage?: string;
  createdBy: string;
}

/**
 * Global hook to monitor all PROCESSING video generations for current user
 * and send notifications when they complete (success or failure).
 * 
 * This runs in the background regardless of which page the user is on.
 */
export function useVideoGenerationNotifications() {
  const { user } = useAuth();
  const trackedGenerationsRef = useRef<Set<string>>(new Set());
  const originalTitleRef = useRef<string>(document.title);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Request notification permission on first load
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('Notification permission:', permission);
      });
    }

    // Restore original title when tab gains focus
    const handleFocus = () => {
      updateTabTitle('restore', originalTitleRef.current);
    };
    window.addEventListener('focus', handleFocus);

    // Cleanup old notification markers (older than 24 hours)
    const cleanupOldNotifications = () => {
      const now = Date.now();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('video_notified_')) {
          const timestamp = parseInt(localStorage.getItem(key) || '0', 10);
          if (now - timestamp > maxAge) {
            localStorage.removeItem(key);
            console.log(`[VideoNotification] Cleaned up old notification marker: ${key}`);
          }
        }
      }
    };
    
    cleanupOldNotifications();
    const cleanupInterval = setInterval(cleanupOldNotifications, 60 * 60 * 1000); // Run every hour

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(cleanupInterval);
    };
  }, []);

  useEffect(() => {
    // Only run if user is logged in
    if (!user || !user.id) {
      return;
    }

    const checkProcessingVideos = async () => {
      try {
        const response = await apiClient.get<{ success: boolean; data: VideoGeneration[] }>(
          '/api/v1/admin/ai-video-generations/processing',
          { params: { userId: user.id } }
        );

        if (!response.success || !response.data) {
          return;
        }

        const processingJobs = response.data;

        // Track which jobs we're currently monitoring
        const currentJobIds = new Set(processingJobs.map(job => job.id));

        // Check for completed/failed jobs that we were tracking
        const completedJobs = Array.from(trackedGenerationsRef.current).filter(
          jobId => !currentJobIds.has(jobId)
        );

        // For completed jobs, fetch their final status and notify
        for (const jobId of completedJobs) {
          try {
            // Check if this job was already notified (by another tab)
            const notifiedKey = `video_notified_${jobId}`;
            const alreadyNotified = localStorage.getItem(notifiedKey);
            
            if (alreadyNotified) {
              console.log(`[VideoNotification] Job ${jobId} already notified by another tab, skipping`);
              trackedGenerationsRef.current.delete(jobId);
              continue;
            }

            const finalResponse = await apiClient.get<{ success: boolean; data: VideoGeneration }>(
              `/api/v1/admin/ai-video-generations/${jobId}`
            );

            if (finalResponse.success && finalResponse.data) {
              const job = finalResponse.data;
              
              // Check again right before notifying (race condition protection)
              if (localStorage.getItem(notifiedKey)) {
                console.log(`[VideoNotification] Job ${jobId} was notified during fetch, skipping`);
                trackedGenerationsRef.current.delete(jobId);
                continue;
              }
              
              // Mark as notified BEFORE sending notifications to prevent race conditions
              localStorage.setItem(notifiedKey, Date.now().toString());
              
              // Get panel, scene, story info for notification
              let notificationBody = `Panel ${job.ownerId}`;
              
              if (job.ownerType === 'PANEL') {
                try {
                  // Fetch panel details to get scene and story info
                  const panelResponse = await apiClient.get<{ success: boolean; data: any }>(
                    `/api/v1/admin/ai-video-generations/panel/${job.ownerId}/details`
                  );
                  
                  if (panelResponse.success && panelResponse.data) {
                    const panel = panelResponse.data;
                    const sceneName = panel.scene?.title || `Scene ${panel.scene?.orderIndex ?? '?'}`;
                    const storyName = panel.sourceStory?.name || 'Unknown Story';
                    
                    const elapsedTime = Math.round(
                      (new Date(job.modifiedAt).getTime() - new Date(job.createdAt).getTime()) / 1000
                    );
                    const minutes = Math.floor(elapsedTime / 60);
                    const seconds = elapsedTime % 60;
                    const timeStr = `${minutes}p${seconds}s`;
                    
                    notificationBody = `${storyName}\nScene: ${sceneName}\nPanel #${panel.sceneOrderIndex + 1} • ${timeStr}`;
                  }
                } catch (error) {
                  console.error('Failed to fetch panel details:', error);
                }
              }

              if (job.status === 'COMPLETED') {
                playNotificationSound('success');
                sendBrowserNotification('✅ Video hoàn thành!', notificationBody);
                updateTabTitle('success');
              } else if (job.status === 'FAILED') {
                playNotificationSound('error');
                sendBrowserNotification(
                  '❌ Video thất bại!',
                  `${notificationBody}\n\n❌ ${job.errorMessage || 'Unknown error'}`
                );
                updateTabTitle('error');
              }

              // Remove from tracked set
              trackedGenerationsRef.current.delete(jobId);
            }
          } catch (error) {
            console.error(`Failed to fetch final status for job ${jobId}:`, error);
            trackedGenerationsRef.current.delete(jobId);
          }
        }

        // Add new processing jobs to tracking
        processingJobs.forEach(job => {
          trackedGenerationsRef.current.add(job.id);
        });

      } catch (error) {
        console.error('Failed to check processing videos:', error);
      }
    };

    // Poll every 60 seconds
    // checkProcessingVideos();
    // pollIntervalRef.current = setInterval(checkProcessingVideos, 60000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user]);
}
